import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import WeakPasswordBanner from "../components/WeakPasswordBanner";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Bell, Check, X, ShieldCheck, UserMinus, UserPlus,
  Users, Building2, ShieldAlert, Briefcase, Mail, FileText, Flag, Trash2, MessageSquare,
  Send, AlertTriangle, CheckCircle2, BadgeCheck,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import useDocumentTitle from "../hooks/useDocumentTitle";

const tone = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  disabled: "bg-zinc-100 text-zinc-700 border-zinc-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("employers");
  useDocumentTitle("Admin panel");

  const refreshStats = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <WeakPasswordBanner />
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">admin panel</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">
            Platform control center
          </h1>
          <p className="text-zinc-600 mt-1">Approve employers, manage users, and keep tabs on growth.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total users" value={stats.total_users} icon={Users} />
            <StatCard label="Pending employers" value={stats.pending_employers} icon={Building2} accent={stats.pending_employers > 0} />
            <StatCard label="Active jobs" value={stats.jobs} icon={Briefcase} />
            <StatCard label="Total applications" value={stats.applications} icon={FileText} />
            <StatCard label="Unread notifications" value={stats.unread_notifications} icon={Bell} accent={stats.unread_notifications > 0} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full sm:w-fit grid-cols-7">
            <TabsTrigger value="employers" data-testid="tab-employers">Employers</TabsTrigger>
            <TabsTrigger value="seekers" data-testid="tab-seekers">Job seekers</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins">Admins</TabsTrigger>
            <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              Notifications
              {stats?.unread_notifications > 0 && (
                <span className="ml-2 tag-mono text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{stats.unread_notifications}</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="employers" className="mt-6">
            <EmployersTab onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="seekers" className="mt-6">
            <SeekersTab onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="jobs" className="mt-6">
            <JobsTab onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="inquiries" className="mt-6">
            <InquiriesTab onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="admins" className="mt-6">
            <AdminsTab currentUser={user} onChange={refreshStats} />
          </TabsContent>
          <TabsContent value="smtp" className="mt-6">
            <SmtpDiagnosticsTab currentUserEmail={user?.email} />
          </TabsContent>
          <TabsContent value="notifications" className="mt-6">
            <NotificationsTab onChange={refreshStats} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className={`p-5 ${accent ? "border-amber-200 bg-amber-50/50" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-amber-600" : "text-zinc-400"}`} />
      </div>
      <div className="font-display text-3xl font-black mt-2">{value}</div>
    </Card>
  );
}

function EmployersTab({ onChange }) {
  const [filter, setFilter] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = { role: "employer" };
    if (filter !== "all") params.status = filter;
    api.get("/admin/users", { params })
      .then(({ data }) => setUsers(data || []))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (uid, path, msg) => {
    try {
      await api.post(`/admin/users/${uid}/${path}`);
      toast.success(msg);
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleVerified = async (uid, currentlyVerified) => {
    try {
      await api.post(`/admin/users/${uid}/set-verified`, null, {
        params: { verified: !currentlyVerified },
      });
      toast.success(currentlyVerified ? "Verified badge removed" : "Employer verified");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h2 className="font-display text-xl font-bold tracking-tight">Employer accounts</h2>
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-md text-xs">
          {["pending", "approved", "rejected", "disabled", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`emp-filter-${f}`}
              className={`px-3 py-1.5 rounded font-medium capitalize transition-colors ${
                filter === f ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : users.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No employers in this view.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {users.map((u) => (
            <div key={u.user_id} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`emp-row-${u.user_id}`}>
              <div>
                <div className="font-semibold text-zinc-950 flex items-center gap-2">
                  {u.name}
                  {u.is_verified_employer && (
                    <span
                      title="Verified employer"
                      data-testid={`emp-verified-badge-${u.user_id}`}
                      className="inline-flex items-center gap-1 text-[10px] tag-mono px-1.5 py-0.5 rounded-full bg-blue-50 text-[#002FA7] border border-blue-200"
                    >
                      <BadgeCheck className="h-3 w-3" /> verified
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 tag-mono">{u.email} · joined {u.created_at?.slice(0,10)}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`tag-mono ${tone[u.status] || ""}`}>{u.status || "unknown"}</Badge>
                {u.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => act(u.user_id, "approve", "Approved")} data-testid={`emp-approve-${u.user_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(u.user_id, "reject", "Rejected")} data-testid={`emp-reject-${u.user_id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {u.status === "approved" && (
                  <>
                    <Button
                      size="sm"
                      variant={u.is_verified_employer ? "outline" : "default"}
                      onClick={() => toggleVerified(u.user_id, !!u.is_verified_employer)}
                      data-testid={`emp-verify-${u.user_id}`}
                      className={u.is_verified_employer
                        ? "border-blue-200 text-[#002FA7] hover:bg-blue-50"
                        : "bg-[#002FA7] hover:bg-[#00227A] text-white"}
                    >
                      <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                      {u.is_verified_employer ? "Remove verification" : "Verify"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(u.user_id, "disable", "Disabled")} data-testid={`emp-disable-${u.user_id}`}>
                      Disable
                    </Button>
                  </>
                )}
                {(u.status === "disabled" || u.status === "rejected") && (
                  <Button size="sm" onClick={() => act(u.user_id, "enable", "Re-enabled")} data-testid={`emp-enable-${u.user_id}`} className="bg-[#002FA7] hover:bg-[#00227A] text-white">
                    Re-enable
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SeekersTab({ onChange }) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = { role: "job_seeker" };
    if (filter !== "all") params.status = filter;
    api.get("/admin/users", { params })
      .then(({ data }) => setUsers(data || []))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (uid, path, msg) => {
    try {
      await api.post(`/admin/users/${uid}/${path}`);
      toast.success(msg);
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h2 className="font-display text-xl font-bold tracking-tight">Job seekers</h2>
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-md text-xs">
          {["active", "disabled", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`seek-filter-${f}`}
              className={`px-3 py-1.5 rounded font-medium capitalize transition-colors ${
                filter === f ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : users.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No seekers in this view.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {users.map((u) => (
            <div key={u.user_id} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`seek-row-${u.user_id}`}>
              <div className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <span className="h-9 w-9 rounded-full bg-zinc-900 text-white grid place-items-center font-bold text-sm">
                    {u.name?.[0]?.toUpperCase() || "U"}
                  </span>
                )}
                <div>
                  <div className="font-semibold text-zinc-950">{u.name}</div>
                  <div className="text-xs text-zinc-500 tag-mono">{u.email} · joined {u.created_at?.slice(0,10)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`tag-mono ${tone[u.status] || ""}`}>{u.status || "active"}</Badge>
                {u.status !== "disabled" ? (
                  <Button size="sm" variant="outline" onClick={() => act(u.user_id, "disable", "Disabled")} data-testid={`seek-disable-${u.user_id}`}>
                    <UserMinus className="h-3.5 w-3.5 mr-1" /> Disable
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => act(u.user_id, "enable", "Re-enabled")} data-testid={`seek-enable-${u.user_id}`} className="bg-[#002FA7] hover:bg-[#00227A] text-white">
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Enable
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function JobsTab({ onChange }) {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [embed, setEmbed] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/jobs", { params })
      .then(({ data }) => setJobs(data || []))
      .finally(() => setLoading(false));
    api.get("/admin/embed-analytics").then(({ data }) => setEmbed(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const flag = async (jobId, title) => {
    const customReason = window.prompt(
      `Send a notice to the employer about "${title}"?\n\nLeave blank to use the default QA-relevance message, or type a custom note:`,
      ""
    );
    if (customReason === null) return;
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/flag`, { reason: customReason || undefined });
      toast.success(data.email_sent ? "Notice sent to employer" : "Job flagged (email could not be sent)");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (jobId, title) => {
    const reason = window.prompt(
      `Remove "${title}" from the platform?\n\nOptional reason:`,
      "Not relevant to QA / Testing / Automation."
    );
    if (reason === null) return;
    try {
      await api.delete(`/admin/jobs/${jobId}`, { params: reason ? { reason } : {} });
      toast.success("Job removed");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reopen = async (jobId) => {
    try {
      await api.post(`/jobs/${jobId}/reopen`);
      toast.success("Job re-opened");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      {embed && (embed.total_impressions > 0 || embed.total_apps_via_embed > 0) && (
        <Card className="p-5 bg-zinc-950 text-white border-zinc-900" data-testid="embed-analytics-card">
          <div className="flex items-center gap-2 mb-3">
            <Flag className="h-4 w-4 text-emerald-400" />
            <span className="text-xs uppercase tracking-widest font-mono text-emerald-400">embed analytics</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-zinc-400 tag-mono">total embed impressions</div>
              <div className="font-display text-3xl font-black mt-1">{embed.total_impressions}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 tag-mono">apps via embed</div>
              <div className="font-display text-3xl font-black mt-1 text-emerald-400">{embed.total_apps_via_embed}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 tag-mono mb-1">top hosts</div>
              <div className="flex flex-wrap gap-1">
                {(embed.by_host || []).slice(0, 5).map(h => (
                  <span key={h.host} className="tag-mono text-[11px] px-2 py-0.5 bg-white/10 rounded">{h.host} · {h.impressions}</span>
                ))}
                {(embed.by_host || []).length === 0 && <span className="text-xs text-zinc-400">no hosts yet</span>}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="font-display text-xl font-bold tracking-tight">All jobs</h2>
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-md text-xs flex-wrap">
          {["active", "closed", "auto_removed", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`jobs-filter-${f}`}
              className={`px-3 py-1.5 rounded font-medium capitalize transition-colors ${
                filter === f ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
              }`}
            >
              {f === "auto_removed" ? "auto removed" : f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No jobs in this view.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {jobs.map((j) => (
            <div key={j.job_id} className="py-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`admin-job-row-${j.job_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/jobs/${j.job_id}`}
                    className="font-semibold text-zinc-950 hover:text-[#002FA7] hover:underline underline-offset-2 truncate"
                    data-testid={`admin-job-title-link-${j.job_id}`}
                  >
                    {j.title}
                  </Link>
                  {j.auto_removed && (
                    <span
                      className="tag-mono text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                      data-testid={`admin-auto-removed-badge-${j.job_id}`}
                      title="Auto-removed by the system (no applications in 30 days)"
                    >
                      auto removed · no-activity
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 tag-mono">
                  {j.company_name} · {j.location} · {j.job_type} · {j.experience_min}–{j.experience_max} yrs · {j.applicants ?? 0} applied
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(j.required_skills || []).slice(0, 6).map(s => (
                    <span key={s} className="tag-mono px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[10px]">{s}</span>
                  ))}
                </div>
                {j.flag_reason && (
                  <div className="mt-1 text-[11px] text-amber-700 tag-mono">⚠ flagged</div>
                )}
                {j.removed_by_admin && (
                  <div className="mt-1 text-[11px] text-red-700 tag-mono">removed: {j.removed_reason}</div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`tag-mono ${j.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                  {j.is_active ? "active" : "closed"}
                </Badge>
                {j.is_active && (
                  <>
                    <Button size="sm" variant="outline" onClick={()=>flag(j.job_id, j.title)} data-testid={`flag-job-${j.job_id}`}>
                      <Flag className="h-3.5 w-3.5 mr-1" /> Notify employer
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>remove(j.job_id, j.title)} data-testid={`remove-job-${j.job_id}`} className="border-red-200 text-red-700 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </>
                )}
                {!j.is_active && (
                  <Button size="sm" onClick={()=>reopen(j.job_id)} data-testid={`reopen-job-${j.job_id}`} className="bg-[#002FA7] hover:bg-[#00227A] text-white">
                    Re-open
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
    </div>
  );
}

function InquiriesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/contacts")
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markHandled = async (contactId) => {
    try {
      await api.post(`/admin/contacts/${contactId}/handled`);
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openReply = (m) => {
    setReplyTarget(m);
    setReplySubject(`Re: your qatestology inquiry — ${m.purpose_label || ""}`.trim());
    setReplyBody(`Hi ${m.name || "there"},\n\nThanks for reaching out to QaTestology.\n\n`);
    setReplyOpen(true);
  };

  const sendReply = async () => {
    if (!replyTarget) return;
    const body = replyBody.trim();
    if (!body) { toast.error("Reply cannot be empty"); return; }
    setSending(true);
    try {
      await api.post(`/admin/contacts/${replyTarget.contact_id}/reply`, {
        subject: replySubject.trim() || undefined,
        body,
      });
      toast.success(`Reply sent to ${replyTarget.email}`);
      setReplyOpen(false);
      setReplyTarget(null);
      setReplyBody("");
      setReplySubject("");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold tracking-tight">Contact form inquiries</h2>
        <span className="text-xs text-zinc-500 tag-mono">{items.length} total</span>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No inquiries yet.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((m) => (
            <div key={m.contact_id} className="py-4" data-testid={`inquiry-row-${m.contact_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-md bg-blue-50 grid place-items-center text-[#002FA7] flex-shrink-0">
                    <MessageSquare className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-950 truncate">{m.name}</div>
                    <div className="text-xs text-zinc-500 tag-mono">
                      {m.email}{m.location ? ` · ${m.location}` : ""} · {m.created_at?.slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 tag-mono">{m.purpose_label}</Badge>
                  {m.replied_at && (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 tag-mono">
                      replied {m.replied_at?.slice(0, 10)}
                    </Badge>
                  )}
                  {m.handled && !m.replied_at ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 tag-mono">handled</Badge>
                  ) : null}
                  <Button
                    size="sm"
                    onClick={() => openReply(m)}
                    data-testid={`inquiry-reply-btn-${m.contact_id}`}
                    className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" /> Reply
                  </Button>
                  {!m.handled && (
                    <Button size="sm" variant="outline" onClick={() => markHandled(m.contact_id)} data-testid={`mark-handled-${m.contact_id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Mark handled
                    </Button>
                  )}
                </div>
              </div>
              <div className="ml-12 bg-zinc-50 border border-zinc-200 rounded p-3 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
                {m.message}
              </div>
              {Array.isArray(m.replies) && m.replies.length > 0 && (
                <div className="ml-12 mt-2 space-y-2">
                  {m.replies.map((r) => (
                    <div key={r.reply_id} className="bg-blue-50/60 border border-blue-100 rounded p-3 text-xs text-zinc-700">
                      <div className="tag-mono text-[10px] text-zinc-500 mb-1">
                        sent by {r.sent_by} · {r.sent_at?.slice(0, 19).replace("T", " ")}
                      </div>
                      <div className="whitespace-pre-wrap leading-relaxed">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="max-w-lg" data-testid="inquiry-reply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Reply to {replyTarget?.name}
            </DialogTitle>
            <DialogDescription>
              Sending an email to <span className="tag-mono">{replyTarget?.email}</span>. They will receive it from our support inbox.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                data-testid="inquiry-reply-subject"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea
                rows={8}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                data-testid="inquiry-reply-body"
                className="mt-1.5"
                placeholder="Type your reply…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              onClick={sendReply}
              disabled={sending}
              data-testid="inquiry-reply-send"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            >
              <Send className="h-4 w-4 mr-1" /> {sending ? "Sending…" : "Send reply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AdminsTab({ currentUser, onChange }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/admins").then(({ data }) => setAdmins(data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("All fields required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Admin added");
      setForm({ email: "", name: "", password: "" });
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("Remove admin access for this user?")) return;
    try {
      await api.delete(`/admin/admins/${uid}`);
      toast.success("Admin removed");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">Add a new admin</h2>
        <p className="text-sm text-zinc-600 mb-4">
          The user will get full admin access. If the email already exists, that account is promoted to admin instead.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e)=>setForm(f=>({...f, name: e.target.value}))} className="mt-1.5" data-testid="new-admin-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email: e.target.value}))} className="mt-1.5" data-testid="new-admin-email" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={(e)=>setForm(f=>({...f, password: e.target.value}))} className="mt-1.5" data-testid="new-admin-password" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={create} disabled={saving} data-testid="add-admin-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
            <ShieldCheck className="h-4 w-4 mr-2" /> {saving ? "Adding…" : "Add admin"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-display text-xl font-bold tracking-tight mb-4">Current admins</h2>
        <div className="divide-y divide-zinc-100">
          {admins.map((a) => (
            <div key={a.user_id} className="py-3 flex items-center justify-between" data-testid={`admin-row-${a.user_id}`}>
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-zinc-900 text-white grid place-items-center">
                  <ShieldAlert className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-semibold text-zinc-950">{a.name} {a.user_id === currentUser.user_id && <span className="text-xs font-normal text-zinc-500">(you)</span>}</div>
                  <div className="text-xs text-zinc-500 tag-mono">{a.email}</div>
                </div>
              </div>
              {a.user_id !== currentUser.user_id && (
                <Button size="sm" variant="outline" onClick={() => remove(a.user_id)} data-testid={`remove-admin-${a.user_id}`}>
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const ICON_BY_TYPE = {
  new_user: Mail,
  new_job_open: Briefcase,
  new_job_closed: X,
  new_inquiry: MessageSquare,
};

const LABEL_BY_TYPE = {
  new_user: (n) => <><span className="font-semibold text-zinc-950">{n.name}</span> signed up as <Badge variant="outline" className="tag-mono ml-1">{n.role}</Badge></>,
  new_job_open: (n) => <><span className="font-semibold text-zinc-950">{n.name}</span> posted a new job <span className="font-semibold text-zinc-950">"{n.job_title}"</span> at {n.company_name}</>,
  new_job_closed: (n) => <><span className="font-semibold text-zinc-950">{n.name}</span> closed the job <span className="font-semibold text-zinc-950">"{n.job_title}"</span></>,
  new_inquiry: (n) => <><span className="font-semibold text-zinc-950">{n.name}</span> sent an inquiry · <Badge variant="outline" className="tag-mono ml-1">{n.purpose_label}</Badge></>,
};

function NotificationsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/notifications")
      .then(({ data }) => setItems(data?.items || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/mark-all-read");
      load();
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold tracking-tight">Activity feed</h2>
        <Button size="sm" variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
          <Check className="h-3.5 w-3.5 mr-1" /> Mark all read
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No notifications yet.</div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((n) => {
            const Icon = ICON_BY_TYPE[n.type] || Mail;
            const Label = LABEL_BY_TYPE[n.type] || ((x) => <span>{x.type}</span>);
            return (
              <div key={n.notification_id} className={`py-3 flex items-center gap-3 ${!n.read ? "" : "opacity-60"}`} data-testid={`notif-row-${n.notification_id}`}>
                <span className={`h-2 w-2 rounded-full ${!n.read ? "bg-[#002FA7]" : "bg-zinc-300"}`} />
                <div className="h-9 w-9 rounded-md bg-blue-50 grid place-items-center text-[#002FA7]">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{Label(n)}</div>
                  <div className="text-xs text-zinc-500 tag-mono">{n.email && `${n.email} · `}{n.created_at?.slice(0, 19).replace("T", " ")}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


function SmtpDiagnosticsTab({ currentUserEmail }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [testTo, setTestTo] = useState(currentUserEmail || "");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/admin/smtp-status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ error: true }))
      .finally(() => setStatusLoading(false));
  }, []);

  useEffect(() => {
    if (currentUserEmail && !testTo) setTestTo(currentUserEmail);
  }, [currentUserEmail, testTo]);

  const runTest = async () => {
    if (!testTo) { toast.error("Enter a destination email first"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/smtp-test", { to_email: testTo });
      setTestResult(data);
      if (data?.ok) {
        toast.success(`Test email sent to ${testTo}`);
      } else {
        toast.error(data?.reason || "SMTP test failed");
      }
    } catch (err) {
      const reason = formatApiError(err.response?.data?.detail) || err.message;
      setTestResult({ ok: false, reason });
      toast.error(reason);
    } finally {
      setTesting(false);
    }
  };

  const configOk = status && status.smtp_user_configured && status.smtp_password_configured && status.smtp_host;

  return (
    <div className="space-y-4">
      <Card className="p-6" data-testid="smtp-status-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight">SMTP configuration</h3>
            <p className="text-xs text-zinc-500 mt-1">
              Live view of the SMTP settings on THIS backend pod. Compare with your preview if production
              emails aren&apos;t being delivered.
            </p>
          </div>
          {status && (
            <span
              data-testid="smtp-config-status"
              className={`tag-mono text-xs px-2 py-1 rounded ${configOk ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}
            >
              {configOk ? (
                <><CheckCircle2 className="inline h-3 w-3 mr-1" /> configured</>
              ) : (
                <><AlertTriangle className="inline h-3 w-3 mr-1" /> missing settings</>
              )}
            </span>
          )}
        </div>
        {statusLoading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : status?.error ? (
          <div className="text-sm text-red-600">Failed to load SMTP status.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <StatusRow k="SMTP host" v={status?.smtp_host || "(not set)"} good={!!status?.smtp_host} />
            <StatusRow k="SMTP port" v={String(status?.smtp_port || "(not set)")} good={!!status?.smtp_port} />
            <StatusRow k="SMTP user" v={status?.smtp_user_masked || "(not set)"} good={status?.smtp_user_configured} />
            <StatusRow k="SMTP password" v={status?.smtp_password_configured ? "•••• configured" : "(not set)"} good={status?.smtp_password_configured} />
            <StatusRow k="Sender email" v={status?.sender_email || "(not set)"} good={!!status?.sender_email} />
            <StatusRow k="App URL" v={status?.app_url || "(not set)"} good={!!status?.app_url} />
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="smtp-test-card">
        <h3 className="font-display text-lg font-bold tracking-tight mb-2">Send a test email</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Fires a real SMTP send through the same pipeline used for verification / top-match emails.
          The raw error message is surfaced below so you can see exactly why Hostinger rejected the message.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            data-testid="smtp-test-to"
            className="flex-1 min-w-[240px]"
          />
          <Button
            onClick={runTest}
            disabled={testing}
            data-testid="smtp-test-btn"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
          >
            <Send className={`h-4 w-4 mr-2 ${testing ? "animate-pulse" : ""}`} />
            {testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        {testResult && (
          <div
            data-testid="smtp-test-result"
            className={`mt-4 p-3 rounded-md border text-sm ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}
          >
            {testResult.ok ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>Delivered to {testResult.to} via {testResult.host}:{testResult.port}. Check the inbox (and spam folder).</span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <div>
                  <div className="font-semibold">SMTP send failed</div>
                  <div className="text-xs font-mono mt-1 break-all">{testResult.reason}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusRow({ k, v, good }) {
  return (
    <div className="flex items-center justify-between p-2 bg-zinc-50 rounded border border-zinc-200">
      <span className="text-xs text-zinc-500 tag-mono">{k}</span>
      <span className={`text-xs tag-mono ${good ? "text-emerald-700" : "text-red-700"}`} data-testid={`smtp-row-${k.replace(/\s+/g, "-").toLowerCase()}`}>
        {v}
      </span>
    </div>
  );
}
